'use client';
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  ArrowRight,
  BellRing,
  BrainCircuit,
  DatabaseZap,
  Eye,
  LoaderCircle,
  MessageCircle,
  PhoneCall,
  ScanLine,
  ShieldCheck,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useT } from "../i18n";
import { sendTrack } from "../lib/track";
import Footer from "../components/layout/Footer";

const container = { hidden: {}, show: { transition: { staggerChildren: .15, delayChildren: .45 } } };
const item = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: .55 } } };

function ActZero({ onComplete }: { onComplete: () => void }) {
  const t = useT();
  const title = t('bizpulse.title');
  const [text, setText] = useState("");
  const [alarm, setAlarm] = useState(false);

  useEffect(() => {
    let index = 0;
    const typing = window.setInterval(() => {
      index += 1;
      setText(title.slice(0, index));
      if (index === title.length) window.clearInterval(typing);
    }, 82);
    const warning = window.setTimeout(() => setAlarm(true), 2100);
    return () => {
      window.clearInterval(typing);
      window.clearTimeout(warning);
    };
  }, [title]);

  return (
    <motion.section className="act act-zero" initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.05, filter: "blur(8px)" }} transition={{ duration: .8 }}>
      <motion.div className="act-bg" animate={alarm ? { scale: [1, 1.025, 1.01, 1.035] } : { scale: 1 }} transition={{ duration: 1.5 }} />
      <div className="shade shade-zero" />
      <motion.div className="red-flash" animate={alarm ? { opacity: [0, .35, .02, .4, 0] } : { opacity: 0 }} transition={{ duration: 1.5 }} />
      <div className="grain" />
      <div className="act-content hero-content">
        <motion.div className="kicker" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: .25 }}><ShieldCheck size={18} /> {t('bizpulse.kicker')}</motion.div>
        <h1>{text}<motion.i animate={{ opacity: [1, 0, 1] }} transition={{ duration: .75, repeat: Infinity }} /></h1>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35 }}>{t('bizpulse.heroBody')} <strong>{t('bizpulse.heroStrong')}</strong></motion.p>
        <motion.button className="hero-cta" onClick={onComplete} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }} whileTap={{ scale: .97 }}>{t('bizpulse.heroCta')} <ArrowRight size={22} /></motion.button>
      </div>
    </motion.section>
  );
}

function ActOne({ onComplete }: { onComplete: () => void }) {
  const t = useT();
  const [clicked, setClicked] = useState<number[]>([]);
  const [active, setActive] = useState<Spot | null>(null);

  const actOneSpots = useMemo(() => [
    { id: 1, no: "01", title: t('bizpulse.one.s1.title'), tag: t('bizpulse.one.s1.tag'), detail: t('bizpulse.one.s1.detail'), cls: "spot-a" },
    { id: 2, no: "02", title: t('bizpulse.one.s2.title'), tag: t('bizpulse.one.s2.tag'), detail: t('bizpulse.one.s2.detail'), cls: "spot-b" },
    { id: 3, no: "03", title: t('bizpulse.one.s3.title'), tag: t('bizpulse.one.s3.tag'), detail: t('bizpulse.one.s3.detail'), cls: "spot-c" },
  ], [t]);
  type Spot = typeof actOneSpots[number];

  useEffect(() => {
    if (clicked.length === 3 && !active) {
      const timer = window.setTimeout(onComplete, 700);
      return () => window.clearTimeout(timer);
    }
  }, [clicked, active, onComplete]);

  const reveal = (spot: Spot) => {
    setActive(spot);
    setClicked((value) => value.includes(spot.id) ? value : [...value, spot.id]);
  };

  return (
    <motion.section className="act act-one" initial={{ opacity: 0, x: "12%" }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: "-10%", filter: "blur(8px)" }} transition={{ duration: .8 }}>
      <div className="act-bg document-bg" />
      <div className="shade shade-one" /><div className="grain" />
      <div className="scan-heading">
        <div className="scan-label"><ScanLine size={18} /> {t('bizpulse.scanLabel')}</div>
        <h2>{t('bizpulse.one.title')}</h2>
        <p>{t('bizpulse.one.body')}</p>
      </div>
      <div className="spot-field">
        {actOneSpots.map((spot, index) => {
          const visited = clicked.includes(spot.id);
          return <motion.button key={spot.id} onClick={() => reveal(spot)} className={`hotspot ${spot.cls} ${visited ? "visited" : ""}`} initial={{ opacity: 0, scale: .6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: .4 + index * .18, type: "spring" }} whileTap={{ scale: .94 }} aria-label={spot.title}>
            <span className="pulse-ring" /><span className="spot-core"><Eye size={24} /></span>
            <b><em>{spot.no}.</em> {spot.title}</b>
          </motion.button>;
        })}
      </div>
      <div className="progress"><span>{t('bizpulse.one.progressPre')} {clicked.length} / 3 {t('bizpulse.one.progressPost')}</span><div>{actOneSpots.map(s => <i key={s.id} className={clicked.includes(s.id) ? "on" : ""} />)}</div></div>
      <AnimatePresence>{active && <motion.div className="insight" initial={{ opacity: 0, y: 30, scale: .96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}>
        <button className="close" onClick={() => setActive(null)} aria-label={t('bizpulse.one.closeAria')}><X size={22} /></button>
        <small>{active.tag}</small><div className="insight-title"><strong>{active.no}</strong><h3>{active.title}</h3></div>
        <p>{active.detail}</p><button className="continue" onClick={() => setActive(null)}>{clicked.length === 3 ? t('bizpulse.one.continueDone') : t('bizpulse.one.continueNext')}</button>
      </motion.div>}</AnimatePresence>
    </motion.section>
  );
}

function ActTwo() {
  const t = useT();
  const [loading, setLoading] = useState(false);
  const openWhatsApp = () => {
    if (loading) return;
    setLoading(true);
    sendTrack({ event: 'whatsapp_click', page: '/bizpulse' });
    setTimeout(() => window.location.href = `https://wa.me/85251651480?text=${encodeURIComponent(t('bizpulse.two.waPrefill'))}`, 500);
  };
  const actTwoSteps = [
    { no: "01", title: t('bizpulse.two.step1.title'), copy: t('bizpulse.two.step1.copy'), icon: DatabaseZap },
    { no: "02", title: t('bizpulse.two.step2.title'), copy: t('bizpulse.two.step2.copy'), icon: BrainCircuit },
    { no: "03", title: t('bizpulse.two.step3.title'), copy: t('bizpulse.two.step3.copy'), icon: BellRing },
    { no: "04", title: t('bizpulse.two.step4.title'), copy: t('bizpulse.two.step4.copy'), icon: PhoneCall },
  ];
  return <motion.section className="act act-two" initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}>
    <div className="act-bg" /><div className="shade shade-two" /><div className="grain" />
    <div className="final-content">
      <motion.div className="final-heading" initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .2 }}><small>{t('bizpulse.two.finalSmall')}</small><h2>{t('bizpulse.two.titlePre')}<br /><span>{t('bizpulse.two.titleHighlight')}</span></h2><p>{t('bizpulse.two.finalBody')}</p></motion.div>
      <motion.div className="steps" variants={container} initial="hidden" animate="show">{actTwoSteps.map(step => { const Icon = step.icon; return <motion.div className="step" variants={item} key={step.no}><span>{step.no}</span><Icon /><h3>{step.title}</h3><p>{step.copy}</p><i /></motion.div>; })}</motion.div>
      <motion.div className="cta-wrap" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25 }}><button onClick={openWhatsApp} disabled={loading}>{loading ? <LoaderCircle className="spin" /> : <MessageCircle fill="currentColor" />} {loading ? t('bizpulse.two.ctaLoading') : t('bizpulse.two.cta')}</button><p>{t('bizpulse.two.note')}</p></motion.div>
    </div>
  </motion.section>;
}

export default function BizpulsePage() {
  const t = useT();
  const [currentAct, setCurrentAct] = useState<0 | 1 | 2>(0);
  const actOne = useCallback(() => setCurrentAct(1), []);
  const actTwo = useCallback(() => setCurrentAct(2), []);

  return (
    <div className="bizpulse-page">
      <main className="deck">
        <header>
          <button className="brand" onClick={() => setCurrentAct(0)} aria-label={t('bizpulse.backAria')}>
            <span><Activity size={16} /></span>
            <div><b>{t('bizpulse.brandName')}</b><small>BIZPULSE</small></div>
          </button>
          <div className="act-nav">
            <small>ACT 0{currentAct + 1}</small>
            <div>{[0, 1, 2].map(n => <i key={n}>{<motion.span animate={{ width: n <= currentAct ? "100%" : 0, opacity: n === currentAct ? 1 : .35 }} />}</i>)}</div>
          </div>
        </header>
        <AnimatePresence mode="wait" initial={false}>
          {currentAct === 0 ? <ActZero key="zero" onComplete={actOne} /> : currentAct === 1 ? <ActOne key="one" onComplete={actTwo} /> : <ActTwo key="two" />}
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
}
